import React, { useState } from "react";
import SEO from "./SEO";

interface FAQItem {
  question: string;
  answer: string;
  category?: string;
}

const FAQ: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>("all");

  const toggleItem = (index: number) => {
    setOpenItems((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index],
    );
  };

  const faqItems: FAQItem[] = [
    {
      question: "What age groups can participate in horse riding?",
      answer:
        "We welcome riders of all ages! Pony rides are perfect for children aged 2-8 years, mini rides are suitable for ages 4+ with adults, and standard rides are available for ages 12+. All rides are guided by experienced staff to ensure safety.",
      category: "riding",
    },
    {
      question: "Do I need prior horse riding experience?",
      answer:
        "No prior experience is required! Our rides are designed for beginners and are guided by experienced staff. We provide all necessary safety equipment and instructions before your ride.",
      category: "riding",
    },
    {
      question: "What safety measures do you have in place?",
      answer:
        "Safety is our top priority. We provide helmets and all necessary safety equipment. All rides are guided by experienced staff, and our horses are carefully selected and well-trained. We maintain strict safety protocols throughout your experience.",
      category: "safety",
    },
    {
      question: "How do I book a horse riding experience?",
      answer:
        "You can book by calling us at +27 69 946 4074, emailing us at info@thestableshorserides.co.za, or using our contact form on the website. We recommend booking in advance, especially for weekends and holidays.",
      category: "booking",
    },
    {
      question: "What should I wear for horse riding?",
      answer:
        "We recommend comfortable, closed-toe shoes (no sandals or flip-flops), long pants, and weather-appropriate clothing. Avoid loose clothing that could get caught. We provide helmets and safety equipment.",
      category: "riding",
    },
    {
      question: "What happens if it rains on the day of my booking?",
      answer:
        "We monitor weather conditions closely. If weather conditions are unsafe for riding, we'll reschedule your booking at no additional cost. We'll contact you as early as possible if we need to reschedule.",
      category: "booking",
    },
    {
      question: "Can I bring my own food for the picnic?",
      answer:
        "Our picnic experiences include delicious food and refreshments. However, if you have specific dietary requirements or would like to bring additional items, please let us know in advance and we'll accommodate your needs.",
      category: "picnic",
    },
    {
      question: "How long do the rides last?",
      answer:
        "Ride durations vary: Pony rides are 10 minutes, mini rides are 30 minutes, and standard rides are 60 minutes. Picnic experiences include additional time for the picnic portion of your experience.",
      category: "riding",
    },
    {
      question: "Can I take photos during my ride?",
      answer:
        "Yes! We encourage you to take photos to capture your memories. However, for safety reasons, we ask that you don't use flash photography near the horses. Our staff can also help take photos of your group.",
      category: "experience",
    },
    {
      question: "What's included in the party packages?",
      answer:
        "Our party packages include horse rides for all guests, a picnic setup with food and refreshments, party decorations, and supervision by our experienced staff. We can customize packages based on your group size and requirements.",
      category: "parties",
    },
    {
      question: "Is there a weight limit for riders?",
      answer:
        "We have horses suitable for riders of different sizes and weights. Please contact us to discuss your specific requirements, and we'll ensure we have the right horse for you.",
      category: "riding",
    },
    {
      question: "Can I feed the horses?",
      answer:
        "Yes! We provide treats for the horses, and our staff will show you the proper way to feed them. This is a great way to interact with the horses and learn more about them.",
      category: "experience",
    },
  ];

  const categories = [
    {
      id: "all",
      name: "All Questions",
      color: "from-stables-500 to-stables-600",
    },
    {
      id: "riding",
      name: "Horse Riding",
      color: "from-stables-500 to-stables-600",
    },
    {
      id: "booking",
      name: "Booking & Info",
      color: "from-stables-500 to-stables-600",
    },
    { id: "safety", name: "Safety", color: "from-stables-500 to-stables-600" },
    {
      id: "parties",
      name: "Parties",
      color: "from-stables-500 to-stables-600",
    },
    { id: "picnic", name: "Picnic", color: "from-stables-500 to-stables-600" },
    {
      id: "experience",
      name: "Experience",
      color: "from-stables-500 to-stables-600",
    },
  ];

  const filteredItems =
    activeCategory === "all"
      ? faqItems
      : faqItems.filter((item) => item.category === activeCategory);

  return (
    <div className="min-h-screen">
      <SEO
        title="FAQ | Horse Riding Questions Answered | The Stables Pretoria East"
        description="Find answers to common questions about horse rides, picnics, parties, safety, booking and more at The Stables in Pretoria East, Boschkop."
        path="/faq"
      />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-cream-50 via-stables-50 to-stables-100">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url(faq.jpg)",
          }}
        ></div>
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-transparent"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl md:text-2xl mb-8 font-light">
            Find answers to common questions about our horse riding experiences
            and services
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-stables-600 hover:bg-stables-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              Contact Us
            </a>
            <a
              href="/horse-rides"
              className="bg-white text-stables-800 hover:bg-stables-100 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300"
            >
              View Rides
            </a>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-stables-800 mb-4">
              Browse by Category
            </h2>
            <p className="text-stables-600 text-lg">
              Find answers to your specific questions
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-lg font-semibold text-base transition-all duration-300 transform hover:scale-105 ${
                  activeCategory === category.id
                    ? `bg-gradient-to-r ${category.color} text-white shadow-2xl`
                    : "bg-white text-stables-700 hover:bg-stables-50 border-2 border-stables-200"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-cream-50 to-stables-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-stables-800 mb-4">
              {activeCategory === "all"
                ? "All Questions"
                : categories.find((c) => c.id === activeCategory)?.name}
            </h2>
            <p className="text-stables-600 text-lg">
              {filteredItems.length} question
              {filteredItems.length !== 1 ? "s" : ""} found
            </p>
          </div>

          <div className="space-y-6">
            {filteredItems.map((item, index) => (
              <div key={index} className="group relative">
                {/* Card Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-stables-600 to-stables-700 rounded-3xl sm:rotate-1 sm:group-hover:rotate-2 transition-transform duration-300"></div>

                {/* Card Content */}
                <div className="relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-stables-200">
                  <button
                    onClick={() => toggleItem(index)}
                    className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-stables-50 transition-colors duration-200 rounded-3xl"
                  >
                    <div className="flex-1">
                      <span className="text-lg font-semibold text-stables-800 leading-relaxed">
                        {item.question}
                      </span>
                      {item.category && (
                        <div className="mt-2">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${
                              categories.find((c) => c.id === item.category)
                                ?.color || "from-stables-500 to-stables-600"
                            }`}
                          >
                            {
                              categories.find((c) => c.id === item.category)
                                ?.name
                            }
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <div
                        className={`w-8 h-8 rounded-full bg-gradient-to-r from-stables-500 to-stables-600 flex items-center justify-center transition-transform duration-200 ${
                          openItems.includes(index) ? "rotate-180" : ""
                        }`}
                      >
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </button>
                  {openItems.includes(index) && (
                    <div className="px-8 pb-6">
                      <div className="border-t border-stables-200 pt-4">
                        <p className="text-stables-700 leading-relaxed text-base">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-stables-500 to-stables-600 rounded-full flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-stables-800 mb-2">
                No questions found
              </h3>
              <p className="text-stables-600">
                Try selecting a different category or contact us directly.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-stables-600 to-stables-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <div className="relative">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-32 h-32 border-2 border-white rounded-full"></div>
              <div className="absolute top-8 right-8 w-16 h-16 border-2 border-white rounded-full"></div>
              <div className="absolute bottom-8 left-8 w-24 h-24 border-2 border-white rounded-full"></div>
            </div>

            <div className="relative z-10">
              <h2 className="text-4xl font-bold mb-6">Still Have Questions?</h2>
              <p className="text-xl mb-8 text-stables-100">
                Can't find the answer you're looking for? Get in touch with us
                and we'll be happy to help!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+27699464074"
                  className="bg-white text-stables-800 hover:bg-cream-50 px-6 py-3 rounded-lg text-base font-semibold transition-colors duration-300 transform hover:scale-105"
                >
                  Call Us
                </a>
                <a
                  href="/contact"
                  className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-white hover:text-stables-600 transition-colors duration-300 transform hover:scale-105"
                >
                  Send Message
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
